import SectionService from "utilities/services/sections/section-service";
import SectionRecord from "models/view-models/section-record";
import { useCallback, useMemo } from "react";
import { useSharedPageContext } from "utilities/contexts/shared-page/shared-page-context";
import SharedPageSectionService from "utilities/services/shared-page-sections/shared-page-section-service";
import usePublications from "../publications/use-publications";
import StringUtils from "utilities/string-utils";
import { atom, useAtom } from "jotai";

const loadingCodeReferenceAtom = atom<boolean>(false);
interface UseCodeReferenceBlockSectionApi {
    getCodeReferenceSection: (
        sectionId?: number
    ) => Promise<SectionRecord | undefined>;
    loadingCodeReference: boolean;
    setLoadingCodeReference: (
        value: boolean | ((prev: boolean) => boolean)
    ) => void;
}

/**
 * Custom hook to get a section with its sub-sections and its publication.
 */
export default function useCodeReferenceBlockSectionApi(): UseCodeReferenceBlockSectionApi {
    const { get: getSectionApi } = SectionService.useGet();
    const { list: listSubsectionsApi } = SectionService.useList();
    const { get: getSharedSectionApi } = SharedPageSectionService.useGet();
    const { list: listSharedSubsectionsApi } =
        SharedPageSectionService.useList();
    const { pageGuid, recipientGuid } = useSharedPageContext();
    const [loadingCodeReference, setLoadingCodeReference] = useAtom(
        loadingCodeReferenceAtom
    );

    const { isPublic, loadSection, loadSubsections } = useMemo(() => {
        if (StringUtils.isEmpty(pageGuid)) {
            return {
                isPublic: false,
                loadSection: (sectionId: number) =>
                    getSectionApi({ id: sectionId }),
                loadSubsections: (sectionId: number) =>
                    listSubsectionsApi({ parentId: sectionId }),
            };
        }

        return {
            isPublic: true,
            loadSection: (sectionId: number) =>
                getSharedSectionApi(
                    { sectionId: sectionId, pageGuid: pageGuid },
                    { recipient: recipientGuid }
                ),
            loadSubsections: (sectionId: number) =>
                listSharedSubsectionsApi(
                    { pageGuid: pageGuid },
                    {
                        parentId: sectionId,
                        recipient: recipientGuid,
                    }
                ),
        };
    }, [
        getSectionApi,
        getSharedSectionApi,
        listSharedSubsectionsApi,
        listSubsectionsApi,
        pageGuid,
        recipientGuid,
    ]);

    const { getPublicationById } = usePublications({ isPublic: isPublic });

    const getCodeReferenceSection = useCallback(
        async (sectionId?: number): Promise<SectionRecord | undefined> => {
            if (sectionId == null) {
                return;
            }
            setLoadingCodeReference(true);
            const sectionResponse = await loadSection(sectionId);
            const subSectionResponse = await loadSubsections(sectionId);
            const publicationResponse = await getPublicationById(
                sectionResponse.resultObject?.publicationId
            );

            const section = sectionResponse.resultObject;
            const subSections = subSectionResponse.resultObjects;
            const publication = publicationResponse?.resultObject;

            if (section == null || publication == null) {
                setLoadingCodeReference(false);

                return;
            }

            const subSectionsWithPublications = subSections.map(
                (section: SectionRecord) =>
                    section.with({
                        publication: publication,
                    })
            );

            const sectionWithSubSectionsAndPublication = section.with({
                subSections: subSectionsWithPublications,
                publication: publication,
            });
            setLoadingCodeReference(false);
            return sectionWithSubSectionsAndPublication;
        },
        [
            setLoadingCodeReference,
            loadSection,
            loadSubsections,
            getPublicationById,
        ]
    );

    return {
        getCodeReferenceSection: getCodeReferenceSection,
        loadingCodeReference: loadingCodeReference,
        setLoadingCodeReference: setLoadingCodeReference,
    };
}
